import React, {useState, useEffect, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'

import {connect} from 'react-redux'

import {get} from 'lodash'

import {
  makeGetArtistBands, 
  selectArtistFetchedAssociations
} from '../../../selectors'

import {useIsMounted, useFetchFavoritesOfType} from '../../../hooks'

import {
  index,
  addBandsToArtist,
  setFetchedAssociations
} from '../../../actions'

import Dialog from '@mui/material/Dialog'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import IconButton from '@mui/material/IconButton'
import Avatar from '@mui/material/Avatar'
import AvatarGroup from '@mui/material/AvatarGroup'

import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import BandRow from '../../cards/BandRow'

import BandForm from '../../band/BandForm'
import LeaveBand from '../../band/LeaveBand'


import {SlideTransition, FadeTransition} from '../../generics'




const styles = {
  root: css`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 40px;
    border-top: 1px solid #333;
    border-bottom: 1px solid #333;
    padding: 15px 0 25px 0;
  `,
  header: css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #fff;
    margin-bottom: 10px;
  `,
  section: css`
    width: 100%;
    padding: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    h4 {
      font-size: 1.2rem;
      font-weight: 500;
      margin: 0;
    }
  `,
  mainContainer: css`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 25px;
    cursor: pointer;
  `,
  truncatedText: css`
    display: -webkit-box;
    -webkit-line-clamp: 10;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `,
  expandedLineup: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 7px;
  `,
  footer: css`
    display: flex;
    justify-content: center;
    width: 100%;
    position: relative;
    min-height: 36px;
    margin-top: 15px;
  `,
  expandMoreContainer: css`
    position: absolute;
    right: 0;
    width: 36px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  `,
  showMoreContainer: css`
    display: flex;
    justify-content: center;
  `,
  showMoreButton: css`
    height: 36px;
    width: 110px;
    border-radius: 18px;
    background: #000;
    color: #3ea5ff;
    font-weight: 500;
    border: none;
    text-transform: uppercase;
    font-size: 11px;
  `,
  dialog: css`
    backdrop-filter: blur(10px);
  `,
}





const ArtistBands = props => {

  const isMounted = useIsMounted()

  const isExtraSmallScreen = useMediaQuery(theme => theme.breakpoints.only('xs'))


  const { 
    artistId,
    bands,
    index,
    links,
    setLinks,
    isOwner,
    isLoaded,
    addBandsToArtist,
    setFetchedAssociations,
  } = props
  

  const alwaysExpanded = bands.length > 0 && bands.length <= 3

  const [isFetched, setIsFetched] = useState(isLoaded)
  const [isFetching, setIsFetching] = useState(false)
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded)

  const [modal, setModal] = useState(false)

  const hasNextPage = (links.pages !== links.page)



  useEffect(() => {
    if (!isFetched && !isFetching) {
      setIsFetching(true)
      setFetchedAssociations('ARTIST', artistId, 'bands')

      index('/artists/' + artistId + '/bands', 'BANDS').then(response => {
        addBandsToArtist(artistId, response.data)
        if (isMounted.current) {
          setIsFetched(true)
          setIsFetching(false)
          if (response.links) {
            setLinks(response.links)
          }
        }
      })
    }
  }, [
    index,
    setLinks,
    artistId,
    isFetched,
    isFetching,
    isMounted,
    addBandsToArtist,
    setFetchedAssociations
  ])





  const fetchMoreBands = useCallback(props => {
    if (hasNextPage) {
      const url = links.next_url.slice(4)

      index(url, 'BANDS').then(response => {
        addBandsToArtist(artistId, response.data)

        if (isMounted.current) {
          if (props.isMounted && props.isMounted.current && props.setIsFetching) {
            props.setIsFetching(false)
          }

          if (response.links) {
            setLinks(response.links)
          }
          
          setIsFetched(true)  
        }
      })
    }
  }, [
    addBandsToArtist,
    artistId,
    index,
    isMounted,
    links.next_url,
    setLinks,
    hasNextPage
  ])



  useFetchFavoritesOfType('band')



  const open = content => () => {
    setModal(content)
  }



  function close() {
    if (isMounted.current) {
      setModal('')
    }
  }



  const toggleExpand = () => {
    if (!alwaysExpanded) {
      setIsExpanded((prev) => !prev);
    }
  }



  
  return ((isFetched && bands.length === 0 && !isOwner)
    ? null 
    : <div css={styles.root}>
        <section css={styles.section}>
          <div css={styles.header}>
            {isFetched
              ? <h4>Bands</h4>
              : <Skeleton variant='h4' width='70px' />
            }

            {isOwner &&
              <div>
                <IconButton fontSize='small' onClick={open('create')}>
                  <AddIcon color='secondary' fontSize='small' />
                </IconButton>

                <IconButton fontSize='small' onClick={open('leave')}>
                  <RemoveIcon color='secondary' fontSize='small' />
                </IconButton>
              </div>
            }
          </div>
        </section>


        <div
          css={styles.mainContainer}
          {...(!alwaysExpanded && {onClick: toggleExpand})}
        >
          {!isFetched && <Skeleton variant='rectangular' width='100%' height={70} />}
          {isFetched &&
            (alwaysExpanded || isExpanded ? (
              <div css={styles.expandedLineup}>
                {bands.map((band) => (
                  <BandRow key={band.id} band={band} />
                ))}
              </div>
            ) : (
              <>
                <AvatarGroup
                  max={4}
                  spacing='small'
                  sx={{
                    '& .MuiAvatarGroup-avatar': {
                      fontSize: '0.9rem',
                      border: '1px solid #000 !important',
                      background: '#333',
                    },
                  }}
                >
                  {bands.map((band) => {
                    const {attributes} = band
                    const imageURL = attributes.imageDerivatives?.small || attributes.externalImageUrl

                    return (
                      <Avatar
                        key={band.id}
                        alt={attributes.name}
                        src={imageURL}
                        sx={{
                          border: '1px solid #000 !important',
                        }}
                      />
                    )
                  })}
                </AvatarGroup>
                <div>
                  <p css={styles.truncatedText}>
                    {bands.map((band) => band.attributes.name).join(', ')}
                  </p>
                </div>
              </>
            ))}
        </div>

        {((!alwaysExpanded && bands.length > 0) || hasNextPage) && (
          <div css={styles.footer}>
            {!alwaysExpanded && bands.length > 0 && (
              <div
                css={[
                  styles.expandMoreContainer,
                  css`
                    transition: transform 0.3s ease;
                    transform: ${isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
                  `,
                ]}
                onClick={toggleExpand}
              >
                <ExpandMoreIcon />
              </div>
            )}
            {hasNextPage && (
              <div css={styles.showMoreContainer}>
                <Button css={styles.showMoreButton} onClick={fetchMoreBands}>
                  {isFetching ? 'Loading...' : 'Show More'}
                </Button>
              </div>
            )}
          </div>
        )}




        <Dialog
          open={modal === 'create'} 
          scroll='body'
          sx={{
            '& .MuiDialog-container': {
              '& .MuiPaper-root': {
                maxWidth: '1050px'
              }
            }
          }}
          css={styles.dialog}
          onClose={close}
          fullScreen={isExtraSmallScreen}
          TransitionComponent={isExtraSmallScreen ? SlideTransition : FadeTransition}
        >
          <BandForm creatorType='artist' creatorId={artistId} close={close} />
        </Dialog>


        <Dialog
          open={modal === 'leave'}
          onClose={close}
          css={styles.dialog}
          maxWidth='xs'
          fullScreen={isExtraSmallScreen} 
          disableScrollLock
          TransitionComponent={isExtraSmallScreen ? SlideTransition : FadeTransition}
        >
          <LeaveBand artistId={artistId} bands={bands} handleClose={close} />
        </Dialog>
      </div>
  )
}


const makeMapStateToProps = () => {
  const getArtistBands = makeGetArtistBands()

  const mapStateToProps = (state, props) => {
    const id = props.artistId
    
    const bands = getArtistBands(state, id)

    const role = get(state, ['user', 'attributes', 'role'])

    const isLoaded = selectArtistFetchedAssociations(state, id).includes('bands')

    
    return {
      bands,
      isLoaded,
      isOwner: ['admin', 'editor'].includes(role) || state.user.account.id === id
    }
  }

  return mapStateToProps
}



const actions = {index, addBandsToArtist, setFetchedAssociations}



export default connect(makeMapStateToProps, actions)(ArtistBands)




